import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddCommentIcon from "@mui/icons-material/AddComment";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import ContextWindowMeter from "../common/ContextWindowMeter";
import type { ProductManagerChatMessage } from "../../hooks/useProductManagerAgentChat";
import type { AgentContextUsage } from "../../types/agentChat";

interface ProductManagerChatPanelProps {
  messages: ProductManagerChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  error: string | null;
  contextWindowExceeded?: boolean;
  contextUsage?: AgentContextUsage | null;
  sessionReady?: boolean;
  sessionInitializing?: boolean;
  onNewChat?: () => void;
  quickPrompts?: string[];
  height?: number | string;
  maximized?: boolean;
  onToggleMaximize?: () => void;
}

const ProductManagerChatPanel: React.FC<ProductManagerChatPanelProps> = ({
  messages,
  onSend,
  loading,
  error,
  contextWindowExceeded = false,
  contextUsage = null,
  sessionReady = true,
  sessionInitializing = false,
  onNewChat,
  quickPrompts = [],
  height = 520,
  maximized = false,
  onToggleMaximize,
}) => {
  const chatDisabled = loading || sessionInitializing || !sessionReady;
  const [input, setInput] = React.useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const hasStreamingAssistant = messages.some(
    (m) => m.role === "assistant" && !m.content.trim(),
  );
  const showGlobalSpinner =
    loading && !sessionInitializing && !hasStreamingAssistant;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim() || chatDisabled) return;
    onSend(input);
    setInput("");
  };

  const panelHeight = maximized ? "calc(100vh - 220px)" : height;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: panelHeight,
        minHeight: maximized ? 400 : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {sessionInitializing ? "Starting session…" : "Catalog assistant chat"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ContextWindowMeter contextUsage={contextUsage} />
          {onNewChat && (
            <Tooltip title="New chat">
              <span>
                <IconButton
                  size="small"
                  onClick={onNewChat}
                  disabled={chatDisabled}
                  aria-label="New chat"
                >
                  <AddCommentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onToggleMaximize && (
            <Tooltip title={maximized ? "Restore layout" : "Maximize chat"}>
              <IconButton
                size="small"
                onClick={onToggleMaximize}
                aria-label={maximized ? "Restore layout" : "Maximize chat"}
              >
                {maximized ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <AgentChatMessageBubble
              role={m.role}
              content={m.content}
              isStreaming={m.role === "assistant" && loading && !m.content.trim()}
              structuredBlockPreset={
                m.role === "assistant" ? "product-manager" : undefined
              }
              emptyAssistantFallback="Details are summarized in the Recent catalog actions panel."
            />
          </Box>
        ))}
        {sessionInitializing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Creating assistant session…
            </Typography>
          </Box>
        )}
        {showGlobalSpinner && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      {error && (
        <Alert
          severity={contextWindowExceeded ? "warning" : "error"}
          sx={{ mx: 2, mb: 1 }}
          action={
            contextWindowExceeded && onNewChat ? (
              <Button color="inherit" size="small" onClick={onNewChat}>
                Start new chat
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}
      {quickPrompts.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {quickPrompts.map((p) => (
            <Typography
              key={p}
              variant="caption"
              component="button"
              onClick={() => onSend(p)}
              disabled={chatDisabled}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1,
                py: 0.25,
                cursor: chatDisabled ? "not-allowed" : "pointer",
                opacity: chatDisabled ? 0.6 : 1,
                bgcolor: "background.paper",
              }}
            >
              {p}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask to list, create, or update catalog entities…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={chatDisabled}
          multiline
          maxRows={4}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={chatDisabled || !input.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ProductManagerChatPanel;
